import { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Polyline, Popup, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Loader2, RefreshCw, AlertCircle, Navigation, Users, ExternalLink, ChevronUp, ChevronDown, Flag, GripVertical, Database, Play, ArrowRight, Square } from "lucide-react";
import { PremiumGate } from "@/components/PremiumGate";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "ve_geocache_v2";
const SESSION_KEY = "ve_today_route";
const ITALY_CENTER = [41.9, 12.5];
const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// ── Session persistence (giro di oggi) ───────────────────────────────────────
function todayDateKey() {
  return new Date().toLocaleDateString("it-IT"); // "28/04/2026"
}
function saveSession(stops, userPos, navStep = null) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ date: todayDateKey(), stops, userPos, navStep }));
  } catch {}
}
function loadSession() {
  try {
    const s = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (s && s.date === todayDateKey()) return s;
  } catch {}
  return null;
}

// ── Google Maps URL helpers ───────────────────────────────────────────────────
function gmapsStopUrl(coords) {
  return `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}&travelmode=driving`;
}
function gmapsRouteUrl(stops, userPos) {
  // Usa solo le tappe con coordinate valide
  const valid = stops.filter(s => s.coords);
  if (!valid.length) return null;
  const pts = valid.map(s => `${s.coords.lat},${s.coords.lng}`);
  const hasGps = !!userPos;
  const origin = hasGps ? `${userPos.lat},${userPos.lng}` : pts[0];
  const destination = pts[pts.length - 1];
  // Se GPS disponibile: tutti gli stop sono waypoint (0…N-1, last=destination)
  // Se no GPS: stop[0] è l'origin, quindi waypoint inizia da stop[1]
  const wpStart = hasGps ? 0 : 1;
  const wps = pts.slice(wpStart, pts.length - 1).slice(0, 8);
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
  if (wps.length > 0) url += `&waypoints=${wps.join("|")}`;
  return url;
}

// ── Cache ─────────────────────────────────────────────────────────────────────
function loadCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); } catch { return {}; }
}
function saveCache(c) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch {}
}

// ── Google Maps Geocoding via backend (chiave sicura server-side) ─────────────
async function geocodeAddress(address, city, axiosConfig = {}) {
  const full = [address, city].filter(Boolean).join(", ");
  if (!full) return null;

  const doGeocode = async (q) => {
    try {
      const res = await axios.get(`${API}/api/geocode`, { params: { address: q }, ...axiosConfig });
      if (res.data?.lat && res.data?.lng) return { lat: res.data.lat, lng: res.data.lng };
    } catch {}
    return null;
  };

  // Tentativo 1: nome + indirizzo + città
  const r1 = await doGeocode(full);
  if (r1) return r1;

  // Tentativo 2: solo città (approssimativo)
  if (city) {
    const r2 = await doGeocode(city);
    if (r2) return { ...r2, approximate: true };
  }
  return null;
}
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// ── Nearest-neighbor TSP ─────────────────────────────────────────────────────
function optimizeRoute(start, stops) {
  if (!stops.length) return [];
  const rem = [...stops];
  const route = [];
  let cur = start;
  while (rem.length) {
    let best = 0;
    let bestD = Infinity;
    rem.forEach((s, i) => {
      const d = (s.coords.lat - cur.lat) ** 2 + (s.coords.lng - cur.lng) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    });
    route.push(rem[best]);
    cur = rem[best].coords;
    rem.splice(best, 1);
  }
  return route;
}

// ── Outcome helpers ───────────────────────────────────────────────────────────
const OUTCOME_CONFIG = {
  pending:    { color: "#0F172A", label: "Da visitare" },
  order:      { color: "#2563EB", label: "Fatto Ordine" },
  visited:    { color: "#10b981", label: "Fatto Visita" },
  not_found:  { color: "#f59e0b", label: "Non Trovato" },
  not_passed: { color: "#94a3b8", label: "Non Passato" },
};

function aptOutcomeKey(apt) {
  if (apt.status !== "completed") return "pending";
  return apt.outcome || "visited";
}

// ── Custom Leaflet icons ──────────────────────────────────────────────────────
function makeNumberIcon(num, color) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:30px;height:30px;border-radius:50%;border:2.5px solid white;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,0.35);">${num}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -18],
  });
}

function makeApproxIcon(num) {
  return L.divIcon({
    className: "",
    html: `<div style="background:#94a3b8;width:30px;height:30px;border-radius:50%;border:2.5px dashed white;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:11px;box-shadow:0 2px 8px rgba(0,0,0,0.25);">~${num}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -18],
  });
}

function makeDotIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });
}

const GPS_ICON = L.divIcon({
  className: "",
  html: `<div style="background:#2563EB;width:16px;height:16px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 3px rgba(37,99,235,0.3);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// ── Sortable stop item ────────────────────────────────────────────────────────
function SortableStop({ stop, index, total, onSetStart, isCurrentStep, onNavigate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stop.apt.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? "0 4px 16px rgba(0,0,0,0.13)" : undefined,
    background: isDragging ? "#f1f5f9" : isCurrentStep ? "#eff6ff" : undefined,
  };
  const oc = OUTCOME_CONFIG[aptOutcomeKey(stop.apt)];
  const hasCoords = !!stop.coords;
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-2 px-3 py-2.5 border-b border-slate-50 last:border-0 ${isCurrentStep ? "border-l-2 border-l-blue-500" : ""}`}>
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="p-1.5 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none select-none flex-shrink-0"
        aria-label="Trascina per riordinare"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      {/* Numero / icona mappa mancante */}
      {hasCoords ? (
        <span className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0"
          style={{ background: isCurrentStep ? "#2563EB" : stop.coords?.approximate ? "#94a3b8" : oc.color }}>
          {index + 1}
        </span>
      ) : (
        <span className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-200 flex-shrink-0"
          title="Indirizzo non trovato sulla mappa">
          <MapPin className="w-3 h-3 text-slate-400" />
        </span>
      )}
      {/* Nome cliente */}
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium truncate block ${isCurrentStep ? "text-blue-700" : "text-[#0F172A]"}`}>
          {stop.client.company || stop.client.name}
        </span>
        {isCurrentStep && (
          <span className="text-[10px] text-blue-500 font-semibold">In navigazione</span>
        )}
        {stop.coords?.approximate && !isCurrentStep && (
          <span className="text-[10px] text-amber-500">Posizione approssimativa (solo città)</span>
        )}
        {!hasCoords && (
          <span className="text-[10px] text-red-400">Indirizzo non trovato</span>
        )}
      </div>
      {/* Naviga qui (solo tappe con coordinate) */}
      {hasCoords && (
        <button onClick={() => onNavigate(index)}
          data-testid={`nav-stop-btn-${index}`}
          title={`Naviga verso ${stop.client.company || stop.client.name}`}
          className={`p-1.5 rounded-xl transition-colors flex-shrink-0 ${isCurrentStep ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-100 text-slate-500 hover:bg-blue-100 hover:text-blue-600"}`}>
          <Navigation className="w-3.5 h-3.5" />
        </button>
      )}
      {/* Parti da qui — solo per tappe con coordinate */}
      {index > 0 && hasCoords && (
        <button onClick={() => onSetStart(index)}
          data-testid={`set-start-btn-${index}`}
          className="px-2 py-0.5 rounded-lg bg-blue-50 text-[#2563EB] text-[10px] font-bold hover:bg-blue-100 transition-colors flex-shrink-0"
          title="Imposta come punto di partenza (#1)">
          <Flag className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function MapsContent() {
  const { token } = useAuth();
  const [mode, setMode] = useState("today");
  const [listOpen, setListOpen] = useState(true); // lista tappe aperta/chiusa

  // Navigazione step-by-step
  const [currentNavStep, setCurrentNavStep] = useState(null);

  // Today mode
  const [todayStops, setTodayStops] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayGeo, setTodayGeo] = useState(false);
  const [todayProgress, setTodayProgress] = useState({ done: 0, total: 0 });
  const [todayError, setTodayError] = useState(null);
  const [userPos, setUserPos] = useState(null);

  // All clients mode (lazy)
  const [allMarkers, setAllMarkers] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allGeo, setAllGeo] = useState(false);
  const [allProgress, setAllProgress] = useState({ done: 0, total: 0 });
  const [dayFilter, setDayFilter] = useState(null);

  const abortRef = useRef(false);

  // ── Session restore (incluso navStep) ────────────────────────────────────
  const restoreNavStep = useCallback((session) => {
    if (session?.navStep !== null && session?.navStep !== undefined) {
      setCurrentNavStep(session.navStep);
    }
  }, []);

  // ── Navigazione step-by-step ───────────────────────────────────────────────
  const navigateToStep = useCallback((index, stops, pos) => {
    const stopsArr = stops || todayStops;
    const stop = stopsArr[index];
    if (!stop?.coords) return;
    window.open(gmapsStopUrl(stop.coords), "_blank");
    setCurrentNavStep(index);
    saveSession(stopsArr, pos || userPos, index);
    setListOpen(true);
  }, [todayStops, userPos]);

  const stopNavigation = useCallback(() => {
    setCurrentNavStep(null);
    saveSession(todayStops, userPos, null);
  }, [todayStops, userPos]);

  // DnD sensors (mouse + touch) ───────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const handleDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setTodayStops(prev => {
      const oldIdx = prev.findIndex(s => s.apt.id === active.id);
      const newIdx = prev.findIndex(s => s.apt.id === over.id);
      const next = arrayMove(prev, oldIdx, newIdx);
      saveSession(next, userPos);
      return next;
    });
  }, [userPos]);

  // ── GPS ───────────────────────────────────────────────────────────────────
  const getGPS = useCallback(() => new Promise(resolve => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { timeout: 6000 }
    );
  }), []);

  // ── Load today route ──────────────────────────────────────────────────────
  const loadToday = useCallback(async (forceRefresh = false) => {
    // Usa la sessione salvata se disponibile (stesso giorno, nessun refresh forzato)
    if (!forceRefresh) {
      const cached = loadSession();
      if (cached) {
        setTodayStops(cached.stops);
        restoreNavStep(cached);
        // Ri-prende il GPS anche da cache: la posizione attuale può essere diversa
        getGPS().then(gps => {
          if (gps) setUserPos(gps);
          else setUserPos(cached.userPos);
        });
        setTodayLoading(false);
        return;
      }
    }

    setTodayLoading(true);
    setTodayError(null);
    setTodayStops([]);
    setCurrentNavStep(null);
    abortRef.current = false;

    try {
      const [aptsRes, gps] = await Promise.all([
        axios.get(`${API}/api/appointments/today`, { headers: { Authorization: `Bearer ${token}` } }),
        getGPS(),
      ]);
      setUserPos(gps);

      // Tutti gli appuntamenti, con o senza cliente
      const allApts = aptsRes.data || [];
      if (!allApts.length) { setTodayLoading(false); return; }

      const cache = loadCache();
      const withAddr = [];  // da geocodificare o già in cache
      const noAddr = [];    // senza indirizzo → lista ma non mappa

      for (const apt of allApts) {
        const c = apt.client;
        if (!c) {
          noAddr.push({ apt, client: { name: apt.title || "Evento", address: "", city: "" }, coords: null });
          continue;
        }
        // 1. Coordinate salvate in DB (massima precisione)
        if (c.lat && c.lng) {
          withAddr.push({ apt, client: c, coords: { lat: c.lat, lng: c.lng } });
          continue;
        }
        const key = `${c.address || ""} ${c.city || ""}`.trim(); // chiave cache
        const geoQuery = [c.company || c.name, c.address].filter(Boolean).join(" "); // query con nome azienda
        if (!key) {
          noAddr.push({ apt, client: c, coords: null });
        } else if (cache[key]) {
          withAddr.push({ apt, client: c, coords: cache[key] });
        } else {
          withAddr.push({ apt, client: c, coords: null, geoKey: key, geoQuery });
        }
      }

      // Geocodifica quelli senza coords (ma con indirizzo)
      const toGeo = withAddr.filter(s => !s.coords);
      const authCfg = { headers: { Authorization: `Bearer ${token}` } };
      if (toGeo.length) {
        setTodayGeo(true);
        setTodayProgress({ done: 0, total: toGeo.length });
        for (let i = 0; i < toGeo.length; i++) {
          if (abortRef.current) break;
          const stop = toGeo[i];
          try {
            const coords = await geocodeAddress(stop.geoQuery || stop.client.address, stop.client.city, authCfg);
            if (coords) {
              cache[stop.geoKey] = coords;
              saveCache(cache);
              stop.coords = coords;
              if (!coords.approximate && stop.client.id) {
                axios.put(`${API}/api/clients/${stop.client.id}`, { lat: coords.lat, lng: coords.lng },
                  authCfg).catch(() => {});
              }
            }
          } catch { /* skip */ }
          setTodayProgress({ done: i + 1, total: toGeo.length });
        }
        setTodayGeo(false);
      }

      // Ottimizza solo quelli con coordinate; quelli senza vanno in fondo
      const geocoded = withAddr.filter(s => s.coords);
      const notGeocoded = withAddr.filter(s => !s.coords);
      const start = gps || geocoded[0]?.coords || { lat: 41.9, lng: 12.5 };
      const optimized = optimizeRoute(start, geocoded);
      const finalStops = [...optimized, ...notGeocoded, ...noAddr];
      setTodayStops(finalStops);
      saveSession(finalStops, gps);
    } catch {
      setTodayError("Impossibile caricare gli appuntamenti di oggi.");
    } finally {
      setTodayLoading(false);
    }
  }, [token, getGPS, restoreNavStep]);

  // ── Load all clients (lazy) ───────────────────────────────────────────────
  const loadAll = useCallback(async (forceRefresh = false) => {
    setAllLoading(true);
    setAllMarkers([]);
    abortRef.current = false;
    try {
      const res = await axios.get(`${API}/api/clients`, { headers: { Authorization: `Bearer ${token}` } });
      const all = (res.data || []).filter(c => c.address || c.city || (c.lat && c.lng));
      const cache = forceRefresh ? {} : loadCache();
      const cKey = c => `${c.address || ""} ${c.city || ""}`.trim();

      // Clienti con coordinate DB → diretti sulla mappa
      const fromDB = all.filter(c => c.lat && c.lng).map(c => ({ client: c, coords: { lat: c.lat, lng: c.lng } }));
      const needGeo = all.filter(c => !(c.lat && c.lng));
      const cached = needGeo.filter(c => cache[cKey(c)]).map(c => ({ client: c, coords: cache[cKey(c)] }));
      setAllMarkers([...fromDB, ...cached]);
      const toGeo = needGeo.filter(c => !cache[cKey(c)]);

      if (toGeo.length) {
        setAllGeo(true);
        setAllProgress({ done: 0, total: toGeo.length });
        const marks = [...fromDB, ...cached];
        const authCfg = { headers: { Authorization: `Bearer ${token}` } };
        for (let i = 0; i < toGeo.length; i++) {
          if (abortRef.current) break;
          const c = toGeo[i];
          try {
            const geoQuery = [c.company || c.name, c.address].filter(Boolean).join(", ");
            const coords = await geocodeAddress(geoQuery || c.address, c.city, authCfg);
            if (coords) {
              cache[cKey(c)] = coords; saveCache(cache); marks.push({ client: c, coords });
              if (!coords.approximate && c.id) {
                axios.put(`${API}/api/clients/${c.id}`, { lat: coords.lat, lng: coords.lng },
                  authCfg).catch(() => {});
              }
            }
          } catch { /* skip */ }
          setAllProgress({ done: i + 1, total: toGeo.length });
          setAllMarkers([...marks]);
        }
        setAllGeo(false);
      }
    } finally {
      setAllLoading(false);
    }
  }, [token]);

  // ── Bulk geocodifica tutti i clienti (dopo loadToday/loadAll) ─────────────
  const [bulkGeo, setBulkGeo] = useState({ running: false, done: 0, total: 0, saved: 0 });

  const runBulkGeo = useCallback(async () => {
    setBulkGeo({ running: true, done: 0, total: 0, saved: 0 });
    const authCfg = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.get(`${API}/api/clients`, authCfg);
      const clients = (res.data || []).filter(c => !(c.lat && c.lng) && (c.address || c.city));
      if (!clients.length) { setBulkGeo({ running: false, done: 0, total: 0, saved: 0 }); return; }
      setBulkGeo({ running: true, done: 0, total: clients.length, saved: 0 });
      const cache = loadCache();
      let saved = 0;
      for (let i = 0; i < clients.length; i++) {
        const c = clients[i];
        const geoQuery = [c.company || c.name, c.address].filter(Boolean).join(", ");
        try {
          const coords = await geocodeAddress(geoQuery || c.address, c.city, authCfg);
          if (coords && !coords.approximate) {
            await axios.put(`${API}/api/clients/${c.id}`, { lat: coords.lat, lng: coords.lng }, authCfg);
            const key = `${c.address || ""} ${c.city || ""}`.trim();
            cache[key] = coords;
            saveCache(cache);
            saved++;
          }
        } catch { /* skip */ }
        setBulkGeo({ running: true, done: i + 1, total: clients.length, saved });
      }
    } finally {
      setBulkGeo(prev => ({ ...prev, running: false }));
      loadToday(true);
    }
  }, [token, loadToday]);

  useEffect(() => {
    loadToday();
    return () => { abortRef.current = true; };
  }, [loadToday]);

  const switchMode = (m) => {
    setMode(m);
    if (m === "all" && allMarkers.length === 0 && !allLoading) loadAll(false);
  };

  // Imposta un cliente come punto di partenza (#1), ricalcola il resto
  const setAsStart = (index) => {
    const start = todayStops[index];
    // Prende solo le tappe con coordinate per riottimizzare
    const withCoords = todayStops.filter((_, i) => i !== index && todayStops[i].coords);
    const withoutCoords = todayStops.filter((_, i) => i !== index && !todayStops[i].coords);
    const optimized = [start, ...optimizeRoute(start.coords, withCoords), ...withoutCoords];
    setTodayStops(optimized);
    saveSession(optimized, userPos);
  };

  const filteredAll = dayFilter === null
    ? allMarkers
    : allMarkers.filter(({ client }) => client.preferred_day === dayFilter);

  // Solo le tappe con coordinate per la linea percorso e i marcatori
  const stopsWithCoords = todayStops.filter(s => s.coords);

  // Calcola l'indice della prossima tappa navigabile (con coordinate)
  const nextNavIndex = (() => {
    if (currentNavStep === null) {
      return todayStops.findIndex(s => s.coords);
    }
    let i = currentNavStep + 1;
    while (i < todayStops.length && !todayStops[i]?.coords) i++;
    return i < todayStops.length ? i : null;
  })();

  const routeLine = [
    ...(userPos ? [[userPos.lat, userPos.lng]] : []),
    ...stopsWithCoords.map(s => [s.coords.lat, s.coords.lng]),
  ];

  const mapCenter = userPos
    ? [userPos.lat, userPos.lng]
    : stopsWithCoords[0]?.coords
    ? [stopsWithCoords[0].coords.lat, stopsWithCoords[0].coords.lng]
    : ITALY_CENTER;

  const mapZoom = (userPos || stopsWithCoords.length > 0) ? 12 : 6;

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Header compatto ── */}
      <div className="px-3 pt-2.5 pb-2 border-b border-slate-200 bg-white flex-shrink-0">

        {/* Riga 1: icona + titolo + Google Maps + refresh */}
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-bold text-[#0F172A] text-sm">
              {mode === "today" ? "Giro di Oggi" : "Mappa Clienti"}
            </span>
            <span className="text-[11px] text-slate-400 ml-2">
              {mode === "today"
                ? todayLoading ? "Caricamento…"
                  : todayGeo ? `${todayProgress.done}/${todayProgress.total} indirizzi…`
                  : todayStops.length > 0
                    ? `${todayStops.length} tappe${stopsWithCoords.length < todayStops.length ? ` · ${stopsWithCoords.length} sulla mappa` : ""}${userPos ? " · GPS" : ""}`
                    : ""
                : allGeo ? `${allProgress.done}/${allProgress.total}…`
                  : `${filteredAll.length} clienti`}
            </span>
          </div>

          {/* Navigazione step-by-step + Google Maps secondario */}
          {mode === "today" && stopsWithCoords.length > 0 && !todayLoading && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Avvia / Prossima tappa */}
              {currentNavStep === null ? (
                <button
                  onClick={() => navigateToStep(todayStops.findIndex(s => s.coords), todayStops, userPos)}
                  data-testid="start-nav-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#2563EB] text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-colors">
                  <Play className="w-3.5 h-3.5" /> Naviga
                </button>
              ) : nextNavIndex !== null ? (
                <button
                  onClick={() => navigateToStep(nextNavIndex, todayStops, userPos)}
                  data-testid="next-stop-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#2563EB] text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-colors">
                  <ArrowRight className="w-3.5 h-3.5" />
                  <span className="max-w-[90px] truncate">
                    {nextNavIndex + 1}. {todayStops[nextNavIndex]?.client?.company || todayStops[nextNavIndex]?.client?.name}
                  </span>
                </button>
              ) : (
                <span className="text-xs text-emerald-600 font-bold px-2.5 py-1.5 bg-emerald-50 rounded-xl">Giro completato</span>
              )}
              {/* Google Maps giro completo (icona secondaria) */}
              <a
                href={gmapsRouteUrl(stopsWithCoords, userPos)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gmaps-route-btn"
                title="Apri tutto il percorso in Google Maps"
                className="p-1.5 text-slate-400 hover:text-blue-600 border border-slate-200 rounded-xl transition-colors flex-shrink-0"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
          <button
            onClick={() => mode === "today" ? loadToday(true) : loadAll(true)}
            disabled={todayLoading || todayGeo || allLoading || allGeo || bulkGeo.running}
            data-testid="maps-refresh-btn"
            className="p-1.5 text-slate-400 hover:text-[#0F172A] border border-slate-200 rounded-xl transition-colors disabled:opacity-40 flex-shrink-0"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${(todayLoading || todayGeo || allLoading || allGeo) ? "animate-spin" : ""}`} />
          </button>

          {/* Geocodifica tutti */}
          <button
            onClick={runBulkGeo}
            disabled={bulkGeo.running || todayLoading || todayGeo}
            data-testid="bulk-geo-btn"
            title="Geocodifica automaticamente tutti i clienti senza posizione"
            className="p-1.5 text-slate-400 hover:text-emerald-600 border border-slate-200 rounded-xl transition-colors disabled:opacity-40 flex-shrink-0"
          >
            <Database className={`w-3.5 h-3.5 ${bulkGeo.running ? "animate-pulse text-emerald-500" : ""}`} />
          </button>
        </div>

        {/* Progress bar geocodifica di massa */}
        {bulkGeo.running && (
          <div className="mt-2 px-1">
            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Geocodifica clienti… {bulkGeo.done}/{bulkGeo.total}
              </span>
              <span className="text-emerald-600 font-semibold">{bulkGeo.saved} salvati</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                style={{ width: bulkGeo.total ? `${(bulkGeo.done / bulkGeo.total) * 100}%` : "0%" }}
              />
            </div>
          </div>
        )}
        {!bulkGeo.running && bulkGeo.total > 0 && (
          <div className="mt-1.5 text-[11px] text-emerald-600 font-medium">
            ✓ Geocodifica completata — {bulkGeo.saved}/{bulkGeo.total} posizioni salvate
          </div>
        )}

        {/* Riga 2: toggle modalità */}
        <div className="flex gap-1.5">
          <button onClick={() => switchMode("today")} data-testid="mode-today"
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${mode === "today" ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            <Navigation className="w-3 h-3" /> Giro di oggi
          </button>
          <button onClick={() => switchMode("all")} data-testid="mode-all"
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${mode === "all" ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            <Users className="w-3 h-3" /> Tutti i clienti
          </button>
        </div>
      </div>

      {/* ── Barra navigazione attiva ── */}
      {mode === "today" && currentNavStep !== null && todayStops[currentNavStep] && (
        <div className="bg-blue-600 text-white px-3 py-2.5 flex items-center gap-2 flex-shrink-0" data-testid="nav-strip">
          <Navigation className="w-4 h-4 flex-shrink-0" style={{ animation: "pulse 2s infinite" }} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold opacity-75 uppercase tracking-wider">
              Tappa {currentNavStep + 1} di {todayStops.length}
            </div>
            <div className="text-sm font-bold truncate leading-tight">
              {todayStops[currentNavStep].client?.company || todayStops[currentNavStep].client?.name}
            </div>
          </div>
          {/* Prossima tappa (se disponibile) */}
          {nextNavIndex !== null && (
            <button
              onClick={() => navigateToStep(nextNavIndex, todayStops, userPos)}
              data-testid="next-stop-strip-btn"
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-colors">
              <ArrowRight className="w-3.5 h-3.5" />
              Prossima
            </button>
          )}
          {/* Stop navigazione */}
          <button
            onClick={stopNavigation}
            data-testid="stop-nav-btn"
            title="Termina navigazione"
            className="p-1.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors flex-shrink-0">
            <Square className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Day filter (all mode) ── */}
      {mode === "all" && (
        <div className="px-4 py-2 bg-white border-b border-slate-100 flex items-center gap-2 flex-shrink-0 overflow-x-auto">
          <span className="text-[11px] text-slate-400 font-medium flex-shrink-0">Giorno:</span>
          <button onClick={() => setDayFilter(null)} data-testid="day-filter-all"
            className={`flex-shrink-0 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${dayFilter === null ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            Tutti
          </button>
          {DAY_LABELS.map((label, idx) => {
            const cnt = allMarkers.filter(({ client }) => client.preferred_day === idx).length;
            return (
              <button key={idx} onClick={() => setDayFilter(dayFilter === idx ? null : idx)}
                data-testid={`day-filter-${idx}`} disabled={cnt === 0}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${dayFilter === idx ? "bg-[#2563EB] text-white" : cnt > 0 ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-slate-50 text-slate-300 cursor-default"}`}>
                {label}{cnt > 0 && <span className={`ml-1 ${dayFilter === idx ? "text-blue-200" : "text-slate-400"}`}>{cnt}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Lista tappe riordinabile (today mode) ── */}
      {mode === "today" && todayStops.length > 0 && !todayLoading && (
        <div className="bg-white border-b border-slate-100 flex-shrink-0">
          {/* Toggle apri/chiudi */}
          <button
            onClick={() => setListOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50"
          >
            <span className="flex items-center gap-1.5">
              {listOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {listOpen ? "Nascondi tappe" : `Mostra ${todayStops.length} tappe`}
            </span>
            <span className="text-slate-400 font-normal">trascina · bandiera · naviga</span>
          </button>

          {listOpen && (
            <div className="border-t border-slate-100 max-h-52 overflow-y-auto">
              {/* Partenza GPS */}
              {userPos && (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-xs text-blue-700">
                  <Navigation className="w-3 h-3 flex-shrink-0" />
                  <span className="font-semibold">La tua posizione (partenza)</span>
                </div>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={todayStops.map(s => s.apt.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {todayStops.map((stop, i) => (
                    <SortableStop
                      key={stop.apt.id}
                      stop={stop}
                      index={i}
                      total={todayStops.length}
                      onSetStart={setAsStart}
                      isCurrentStep={currentNavStep === i}
                      onNavigate={(idx) => navigateToStep(idx, todayStops, userPos)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
      )}

      {/* ── Legend ── */}
      <div className="px-4 py-1.5 bg-white border-b border-slate-100 flex flex-wrap gap-3 text-[11px] text-slate-500 flex-shrink-0">
        {Object.entries(OUTCOME_CONFIG).map(([k, { color, label }]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
            {label}
          </div>
        ))}
      </div>

      {/* ── Map area ── */}
      <div className="flex-1 relative min-h-0" data-testid="maps-container" style={{ minHeight: 0 }}>

        {/* Loading overlays */}
        {mode === "today" && todayLoading && todayStops.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">Calcolo giro di oggi…</p>
          </div>
        )}
        {mode === "all" && allLoading && allMarkers.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">Caricamento mappa…</p>
          </div>
        )}

        {/* Error */}
        {mode === "today" && todayError && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg max-w-xs border border-slate-200 mx-4">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-3">{todayError}</p>
              <button onClick={loadToday} className="text-xs text-[#2563EB] underline">Riprova</button>
            </div>
          </div>
        )}

        {/* Map */}
        {!todayError && ((mode === "today" && !todayLoading) || (mode === "all" && !allLoading)) && (
          <MapContainer
            center={mode === "today" ? mapCenter : ITALY_CENTER}
            zoom={mode === "today" ? mapZoom : 6}
            style={{ height: "100%", width: "100%" }}
            key={mode}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* ── TODAY MODE ── */}
            {mode === "today" && (
              <>
                {routeLine.length >= 2 && (
                  <Polyline
                    positions={routeLine}
                    pathOptions={{ color: "#2563EB", weight: 2.5, opacity: 0.55, dashArray: "8 6" }}
                  />
                )}
                {userPos && (
                  <Marker position={[userPos.lat, userPos.lng]} icon={GPS_ICON}>
                    <Popup><p className="text-xs font-semibold">La tua posizione</p></Popup>
                  </Marker>
                )}
                {stopsWithCoords.map((stop, i) => {
                  const oc = OUTCOME_CONFIG[aptOutcomeKey(stop.apt)];
                  const isApprox = stop.coords?.approximate;
                  return (
                    <Marker
                      key={stop.apt.id}
                      position={[stop.coords.lat, stop.coords.lng]}
                      icon={isApprox ? makeApproxIcon(i + 1) : makeNumberIcon(i + 1, oc.color)}
                    >
                      <Popup>
                        <div className="text-xs space-y-1 min-w-[160px]">
                          <p className="font-bold text-[#0F172A] text-sm">
                            {i + 1}. {stop.client.company || stop.client.name}
                          </p>
                          {stop.client.company && <p className="text-slate-500">{stop.client.name}</p>}
                          <p className="text-slate-500">
                            {stop.client.address}{stop.client.city ? `, ${stop.client.city}` : ""}
                          </p>
                          {isApprox && (
                            <p className="text-amber-500 text-[10px]">~ Posizione approssimativa (solo città)</p>
                          )}
                          <div className="flex items-center justify-between pt-1 gap-2">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full" style={{ background: oc.color }} />
                              <span className="font-medium">{oc.label}</span>
                            </div>
                            <a
                              href={gmapsStopUrl(stop.coords)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 bg-[#2563EB] text-white rounded-lg text-[10px] font-semibold hover:bg-blue-700 transition-colors"
                            >
                              <Navigation className="w-3 h-3" /> Naviga
                            </a>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </>
            )}

            {/* ── ALL CLIENTS MODE ── */}
            {mode === "all" && filteredAll.map(({ client, coords }) => {
              const color = OUTCOME_CONFIG[client.last_outcome]?.color || OUTCOME_CONFIG.pending.color;
              return (
                <Marker key={client.id} position={[coords.lat, coords.lng]} icon={makeDotIcon(color)}>
                  <Popup>
                    <div className="text-xs space-y-1 min-w-[150px]">
                      <p className="font-bold text-[#0F172A] text-sm">{client.name}</p>
                      {client.company && <p className="text-slate-500">{client.company}</p>}
                      <p className="text-slate-500">{client.address}{client.city ? `, ${client.city}` : ""}</p>
                      {client.preferred_day != null && (
                        <p className="text-slate-400">Giorno: <span className="font-medium text-[#2563EB]">{DAY_LABELS[client.preferred_day]}</span></p>
                      )}
                      <a
                        href={gmapsStopUrl(coords)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 bg-[#2563EB] text-white rounded-lg text-[10px] font-semibold hover:bg-blue-700 transition-colors w-fit mt-1"
                      >
                        <Navigation className="w-3 h-3" /> Naviga
                      </a>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}

        {/* Geocoding badge (all mode) */}
        {mode === "all" && allGeo && allMarkers.length > 0 && (
          <div className="absolute top-3 right-3 z-[1000] bg-white/90 backdrop-blur-sm border border-slate-200 rounded-xl px-3 py-2 flex items-center gap-2 shadow-sm text-xs text-slate-600">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2563EB]" />
            Nuovi indirizzi {allProgress.done}/{allProgress.total}
          </div>
        )}

        {/* Empty: no appointments today */}
        {mode === "today" && !todayLoading && !todayError && todayStops.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1000]">
            <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-2xl p-6 text-center shadow-lg max-w-xs mx-4">
              <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-[#0F172A] text-sm">Nessun appuntamento oggi</p>
              <p className="text-xs text-slate-500 mt-1">Non ci sono visite programmate per oggi.</p>
            </div>
          </div>
        )}

        {/* Empty: no clients for this day */}
        {mode === "all" && !allLoading && filteredAll.length === 0 && allMarkers.length > 0 && dayFilter !== null && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1000]">
            <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-2xl p-6 text-center shadow-lg max-w-xs mx-4">
              <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-[#0F172A] text-sm">Nessun cliente il {DAY_LABELS[dayFilter]}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MapsPage() {
  return (
    <PremiumGate>
      <MapsContent />
    </PremiumGate>
  );
}
